import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useDataRecipientsCatalog,
  useOrganizationVendors,
  usePrimaryOrganization,
} from "../../../hooks";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import {
  Breadcrumbs,
  Button,
  Modal,
  Slideout,
  TextInputBase,
  useSnackBar,
} from "@truevault/aquilo";
import { Routes } from "../../../root/routes";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import clsx from "clsx";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightCircleIcon,
  ArrowRightIcon,
  ArrowsUpDownIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
  OrganizationDataSourceDto,
  AddOrUpdateCustomVendorDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { CircularProgress } from "@mui/material";
import { useActionRequest } from "../../../../common/hooks/api";
import { useOnboardingProgress } from "../../../../common/hooks/useOnboardingProgress";
import { Api } from "../../../../common/service/Api";
import { VendorCategoryGroupMode } from "../../../util/vendorCategorization";
import { BadgedTextPill } from "../vendor-documentation/VendorDocumentationPage";
import { getDataSellingCell, getTargetedAdsCell } from "./vendorInventoryTableCellDisplay";
import { useOrgDataSources } from "../../../hooks/useOrganizationVendors";
import {
  categoryPopularityRank,
  isPopularCategory,
  QUICK_ADD_VENDOR_NAMES,
  VENDOR_DISPLAY_LABELS,
} from "./vendorPopularity";
import { KNOWN_EMPLOYMENT_VENDOR_CATEGORIES } from "../../../components/organisms/vendor/VendorCategories";
import {
  useEmploymentCollectionGroups,
  useNonEmploymentCollectionGroups,
} from "../../../hooks/useCollectionGroups";
import { AddVendorModal, SelectedVendor } from "./AddVendorModal";
import { VendorDetailsModal } from "./VendorDetailsModal";
import {
  shouldShowVendorListOnboardingFooter,
  VendorListCompletion,
  vendorListJustCompleted,
} from "./vendorListOnboardingFooter";
import { RemoveVendorConfirmationModal } from "./RemoveVendorConfirmationModal";
import { VendorDataSourceRecipientSection } from "./VendorDataSourceRecipientSection";
import { VendorTargetedAdvertisingSection } from "./VendorTargetedAdvertisingSection";
import { ConnectedSystemVendorsSection } from "./ConnectedSystemVendorsSection";
import { isConnectedSystemVendor } from "../../../util/vendorNesting";
import {
  buildVendorInventoryItems,
  doesVendorHaveSellingComplete,
  doesVendorHaveSharingComplete,
  hasGoogleAnalyticsDataSharingAnswer,
  hasInventoryCollectionContext,
  isVendorRemovable,
  removeVendorCompletely,
  shouldShowInInventoryContext,
  shouldShowDataSellingSection,
  shouldShowSharing,
  vendorRemovalRequiresPageReload,
  VendorInventoryItem,
} from "./vendorInventoryUtils";
import {
  SOURCE_KEY_CONSUMER_DIRECTLY,
  SOURCE_KEY_OTHER_INDIVIDUALS,
  VENDOR_KEY_SHOPIFY_AUDIENCES,
  VENDOR_KEY_TRUEVAULT,
} from "../../../types/Vendor";
import { SurveyAnswers, useSurvey } from "../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../get-compliant/data-recipients/shared";
import {
  addRecipientContextSettings,
  DATA_STORAGE_LOCATION_CATEGORY,
  findGoogleAnalytics,
  isLegacyContractorCategory,
  isShopify,
  isStorageLocation,
} from "../../../util/vendors";
import { SHOPIFY_SNI_SLUG } from "../../../surveys/sellingSurvey";
import { useTrueScan } from "../../../hooks/useTrueScan";
import { VendorDto } from "../../../../common/service/server/dto/VendorDto";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { useHistory, useLocation } from "react-router";
import { TrueScanResultsDto } from "../../../../common/service/server/dto/TrueScanResultsDto";
import { Loading, PageLoading } from "../../../components";
import { VendorDataSellingSection } from "./VendorDataSellingSection";
import { hasShopifySNIAnswer, ShopifySNIQuestion } from "./ShopifySNIQuestion";

export type VendorInventoryLocationState = {
  openAddVendorModal?: boolean;
  preselectedVendorIds?: string[];
};

export type VendorInventoryPageConfig = {
  collectionContext: CollectionContext;
  categoryGroupMode: VendorCategoryGroupMode;
  breadcrumbTitle: string;
  breadcrumbSource: string;
  breadcrumbSourceRoute: string;
  includeEmptyContext: boolean;
  showConsumerOnboarding: boolean;
  showIncompleteVendorWarning: boolean;
  showConsumerOnlyCustomVendorsAndStorageLocations?: boolean;
  addVendorInfoTitle?: string;
  addVendorInfoBody?: string;
  /** Header help text: what belongs in this list, and where sources live instead. */
  recipientsHelpExamples: string;
  sourcesRoute: string;
  sourcesLinkLabel: string;
};

/**
 * The per-vendor slideout is hidden for now: clicking a row opens Vendor Settings directly, which
 * has every setting the slideout had. The slideout code stays so it can be brought back by
 * flipping this flag.
 */
const SHOW_VENDOR_SLIDEOUT = false;

export type VendorInventoryFooterState = {
  showFooter: boolean;
  nextDisabled: boolean;
  isSidebarOpen: boolean;
  hasOnlyTruevaultVendor: boolean;
};

const CONSUMER_VENDOR_INVENTORY_CONFIG: VendorInventoryPageConfig = {
  collectionContext: "CONSUMER",
  categoryGroupMode: "consumer",
  breadcrumbTitle: "Vendor List",
  breadcrumbSource: "Data Map",
  breadcrumbSourceRoute: Routes.dataMap.dashboard,
  includeEmptyContext: true,
  showConsumerOnboarding: true,
  showIncompleteVendorWarning: true,
  recipientsHelpExamples: "service providers and ad platforms",
  sourcesRoute: Routes.dataMap.dataFlows.sources,
  sourcesLinkLabel: "Data Flows → Sources",
};

const isConsumerOnlyContext = (collectionContext: CollectionContext[]): boolean =>
  hasInventoryCollectionContext(collectionContext, "CONSUMER") &&
  !hasInventoryCollectionContext(collectionContext, "EMPLOYEE");

export const isVendorInventoryComplete = (
  vendor: OrganizationDataRecipientDto,
  sellingAndSharingAnswers: SurveyAnswers,
  pageContext: CollectionContext,
) => {
  if (shouldShowSharing(vendor, pageContext) && !doesVendorHaveSharingComplete(vendor)) {
    return false;
  }

  if (
    shouldShowDataSellingSection(vendor, pageContext) &&
    !doesVendorHaveSellingComplete(vendor, sellingAndSharingAnswers)
  ) {
    return false;
  }

  // Shopify's setup includes the "Has your business turned off SNI?" question; until it's
  // answered the vendor's settings are incomplete (POL-7579).
  if (isShopify(vendor) && !hasShopifySNIAnswer(sellingAndSharingAnswers)) {
    return false;
  }

  return true;
};

// Shared column layout for the vendor inventory tables. The pinned header bar and each per-category mini-table
// use the same fixed column widths so their columns line up.
function VendorTableColgroup() {
  return (
    // Column order: Vendor Name, Targeted Advertising, Data Selling, Arrow. (Comments must not sit inline next
    // to the <col> tags — the same-line whitespace becomes a text node, which is invalid inside <colgroup>.)
    <colgroup>
      <col style={{ width: "473px" }} />
      <col style={{ width: "310px" }} />
      <col style={{ width: "310px" }} />
      <col style={{ width: "50px" }} />
    </colgroup>
  );
}

// "What goes in my Vendor Inventory?" explainer modal (Figma node 95:95027). Purple circle-checks for what to
// include, a red circle-X for what to exclude.
function WhoShouldBeInInventoryModal({ onClose }: { onClose: () => void }) {
  const includedItems: { title: string; body: string }[] = [
    {
      title: "Vendors used for business purposes",
      body: "All vendors used for your business, including marketing, advertising, and customer support providers, etc.",
    },
    {
      title: "Third party data recipients",
      body: "Any parties you send data to, such as business partners, co-marketers, data buyers, or affiliates.",
    },
    {
      title: "Contractors",
      body: "Individual contractors (e.g., a developer, consultant, lawyer), Marketing Agencies running your ad campaigns, or outside vendors with access to your Shopify.",
    },
    {
      title: "Data storage location",
      body: "Add an internal storage location, such as a self-managed application or database that stores customer data",
    },
  ];
  const excludedItem = {
    title: "HR Vendors",
    body: "Don't include any HR vendors or employment related vendors. Those will be listed in your HR Data Map.",
  };

  const renderItem = (item: { title: string; body: string }, isExcluded: boolean) => (
    <div key={item.title} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div
        style={{
          backgroundColor: isExcluded ? "#E12037" : "#6E50D7",
          width: 32,
          height: 32,
          borderRadius: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {isExcluded ? (
          <XMarkIcon className="w-5 h-5 text-white" />
        ) : (
          <CheckIcon className="w-5 h-5 text-white" />
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            color: "#1A1726",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: "16px",
            letterSpacing: "0.3px",
            textTransform: "uppercase",
          }}
        >
          {item.title}
        </div>
        <div style={{ color: "#726D83", fontSize: 14, fontWeight: 400, lineHeight: "20px" }}>
          {item.body}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      alignText="left"
      alignButton="right"
      secondButton="Done"
      secondButtonColor="primary"
      onSecondButtonClick={onClose}
      onClose={onClose}
      panelStyle={{ maxWidth: 480 }}
      disableSmFullWidth
      showOverlay
      closeOnOverlayClick
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, textAlign: "left" }}>
        <div style={{ color: "#111827", fontSize: 18, fontWeight: 500, lineHeight: "28px" }}>
          What goes in your Vendor Inventory?
        </div>
        <div style={{ color: "#6B7280", fontSize: 14, fontWeight: 400, lineHeight: "21px" }}>
          Include all vendors, parties, and storage locations where customer data goes. You can
          exclude vendors that only collect anonymized (aggregated) or publicly available customer
          data.
        </div>
        {includedItems.map((item) => renderItem(item, false))}
        {renderItem(excludedItem, true)}
      </div>
    </Modal>
  );
}

export function VendorInventoryPage() {
  const history = useHistory();
  const [org, ,] = usePrimaryOrganization();
  const [footerState, setFooterState] = useState<VendorInventoryFooterState>({
    showFooter: false,
    nextDisabled: true,
    isSidebarOpen: false,
    hasOnlyTruevaultVendor: false,
  });

  // Keep the onboarding footer available for the whole data-map onboarding stretch, not just when
  // incomplete vendors happen to exist at page load — otherwise a user who returns to a finished
  // inventory mid-onboarding has no forward path to Data Flows (DES-30).
  const [onboardingProgress] = useOnboardingProgress(org?.id);
  const isDataMapOnboardingIncomplete =
    onboardingProgress != null &&
    (!onboardingProgress.dataMapOnboardingProgress.isVendorInventoryComplete ||
      !onboardingProgress.dataMapOnboardingProgress.isDataFlowsComplete);

  const snackbar = useSnackBar();
  const finishedOnboardingLatch = org?.finishedOnboardingLatch === true;
  const announceVendorListComplete = useCallback(
    ({
      vendorName,
      showOnboardingFooter,
    }: VendorListCompletion & { showOnboardingFooter: boolean }) => {
      // It points at Done with Vendor List, so only when the footer is there to click.
      if (
        !shouldShowVendorListOnboardingFooter({
          finishedOnboardingLatch,
          showFooter: showOnboardingFooter,
          isDataMapOnboardingIncomplete,
          isSidebarOpen: false,
        })
      ) {
        return;
      }
      snackbar(vendorName ? `${vendorName} is set up.` : "Every vendor is set up.", {
        variant: "success",
        body: "Your Vendor List is complete. Click Done with Vendor List to continue.",
      });
    },
    [finishedOnboardingLatch, isDataMapOnboardingIncomplete, snackbar],
  );

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        shouldShowVendorListOnboardingFooter({
          finishedOnboardingLatch,
          showFooter: footerState.showFooter,
          isDataMapOnboardingIncomplete,
          isSidebarOpen: footerState.isSidebarOpen,
        }) && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Vendor List"
                nextOnClick={() => history.push(Routes.dataMap.dataFlows.dashboard)}
                nextDisabled={footerState.nextDisabled}
                nextDisabledTooltip="Finish setting up all your vendors to continue."
              />
            }
          />
        )
      }
    >
      <div className="tw-boundary">
        <div className="mb-[32px]">
          <Breadcrumbs
            hideHomeIcon={true}
            title={CONSUMER_VENDOR_INVENTORY_CONFIG.breadcrumbTitle}
            source={CONSUMER_VENDOR_INVENTORY_CONFIG.breadcrumbSource}
            sourceRoute={CONSUMER_VENDOR_INVENTORY_CONFIG.breadcrumbSourceRoute}
          />
        </div>
      </div>

      <VendorInventoryPageContent
        config={CONSUMER_VENDOR_INVENTORY_CONFIG}
        onFooterStateChange={setFooterState}
        onVendorListComplete={announceVendorListComplete}
      />
    </StandardPageWrapper>
  );
}

export function VendorInventoryPageContent({
  config,
  onFooterStateChange,
  onVendorListComplete,
}: {
  config: VendorInventoryPageConfig;
  onFooterStateChange?: (footerState: VendorInventoryFooterState) => void;
  /** Called once every vendor is set up, after the settings that finished the list close. */
  onVendorListComplete?: (
    completion: VendorListCompletion & { showOnboardingFooter: boolean },
  ) => void;
}) {
  const history = useHistory();

  const [org, ,] = usePrimaryOrganization();
  const collectionContext = useMemo(() => [config.collectionContext], [config.collectionContext]);
  const [allVendors, allVendorsRequest, refreshVendors] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ true,
    /* excludeIncomplete = */ undefined,
    /* context = */ collectionContext,
    /* onlyUseSettled = */ true,
    /* includeEmptyContext = */ config.includeEmptyContext,
  );
  const [allOrganizationVendors, allOrganizationVendorsRequest] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ true,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );
  const [vendorCatalog, vendorCatalogRequest, refreshVendorCatalog] = useDataRecipientsCatalog(
    org?.id,
    /* includeDataStorageLocations = */ true,
  );

  const snackbar = useSnackBar();

  const [showOnboardingFooter, setShowOnboardingFooter] = useState(false);
  const [showOnboardingView, setShowOnboardingView] = useState(false);

  const [scanInProgress, setScanInProgress] = useState<boolean>(false);
  const [hasEverScanned, setHasEverScanned] = useState<boolean | null>(null);
  const { getScan: getTrueScanDetails } = useTrueScan();
  const [trueScanDetailsLoaded, setTrueScanDetailsLoaded] = useState(false);

  const refreshScanStatus = useCallback(async () => {
    if (!config.showConsumerOnboarding) return null;
    if (!org?.id) return null;
    try {
      const status = await Api.trueScan.hasEverScanned(org.id);
      setScanInProgress(status.inProgress);
      setHasEverScanned(status.hasEverScanned);
      return status;
    } catch (error) {
      console.error("Failed to fetch TrueScan status:", error);
      return null;
    }
  }, [org?.id, config.showConsumerOnboarding]);

  useEffect(() => {
    if (!config.showConsumerOnboarding) return;
    void refreshScanStatus();
  }, [config.showConsumerOnboarding, refreshScanStatus]);

  useEffect(() => {
    if (!config.showConsumerOnboarding) return;
    if (!scanInProgress) return;
    const interval = setInterval(async () => {
      const status = await refreshScanStatus();
      if (status && !status.inProgress) {
        window.location.reload();
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [config.showConsumerOnboarding, scanInProgress, refreshScanStatus]);

  // Declared before the init effect below, which needs the survey request's settled state.
  const {
    answers: sellingAndSharingAnswers,
    request: sellingAndSharingRequest,
    setAnswer: setSellingAndSharingAnswer,
    refresh: refreshSellingAndSharingAnswers,
  } = useSurvey(org?.id, SELLING_AND_SHARING_SURVEY_NAME);

  const [vendorsState, setVendorsState] = useState<OrganizationDataRecipientDto[] | null>(null);
  useEffect(() => {
    // Only initialize once every input has actually loaded, so the onboarding decisions below don't latch a
    // wrong value from a half-loaded state (this effect runs exactly once — `vendorsState === null` — and is
    // never revisited):
    // - `allVendorsRequest.success` gates on the vendor request completing (the hook returns [] while loading,
    //   so truthiness alone races with the scan-status load);
    // - `allVendors` itself can still be undefined while `success` is stale from a previous fetch
    //   (`onlyUseSettled` clears the result without resetting the request state), which would crash the
    //   `.filter` below;
    // - `sellingAndSharingRequest.success` gates on the survey request completing — `sellingAndSharingAnswers`
    //   is `{}` while loading (never undefined), and computing completeness from empty answers would wrongly
    //   count answer-dependent vendors (e.g. Google Analytics) as incomplete.
    const onboardingStatusLoaded = !config.showConsumerOnboarding || hasEverScanned !== null;
    if (
      allVendorsRequest.success &&
      allVendors &&
      sellingAndSharingRequest.success &&
      onboardingStatusLoaded &&
      vendorsState === null
    ) {
      setVendorsState(allVendors);

      const incompleteVendors =
        config.showIncompleteVendorWarning && sellingAndSharingAnswers
          ? allVendors.filter(
              (v) =>
                !isStorageLocation(v) &&
                !isVendorInventoryComplete(v, sellingAndSharingAnswers, config.collectionContext),
            )
          : [];

      if (config.showConsumerOnboarding && allVendors.length === 1 && !hasEverScanned) {
        setShowOnboardingView(true);
        setShowOnboardingFooter(true);
      } else if (config.showIncompleteVendorWarning && incompleteVendors?.length > 0) {
        setShowOnboardingFooter(true);
      }
    }
  }, [
    allVendorsRequest.success,
    allVendors,
    sellingAndSharingRequest.success,
    config.collectionContext,
    config.showConsumerOnboarding,
    config.showIncompleteVendorWarning,
    hasEverScanned,
    sellingAndSharingAnswers,
    vendorsState,
    setVendorsState,
  ]);

  const location = useLocation<VendorInventoryLocationState | undefined>();
  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);
  const [settingsOpenForVendorId, setSettingsOpenForVendorId] = useState<string | null>(null);
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] = useState(false);
  // When the Add modal was opened from an empty category table, scope it to that category.
  const [addVendorCategory, setAddVendorCategory] = useState<string | null>(null);
  // When another page links here with vendors to add, seed the modal with those vendors.
  const [initialSelectedVendors, setInitialSelectedVendors] = useState<
    SelectedVendor[] | undefined
  >(undefined);
  const openAddVendorModal = (category: string | null = null) => {
    setAddVendorCategory(category);
    setInitialSelectedVendors(undefined);
    setIsAddVendorModalOpen(true);
  };
  const closeAddVendorModal = () => {
    setIsAddVendorModalOpen(false);
    setAddVendorCategory(null);
    setInitialSelectedVendors(undefined);
  };
  const [isWhoModalOpen, setIsWhoModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // First-run help is dismissible for this page load. Its durable visibility is derived from inventory size,
  // so it reappears after a refresh while the organization still has 10 or fewer non-storage vendors.
  const [onboardingHelpDismissed, setOnboardingHelpDismissed] = useState(false);
  const dismissOnboardingHelp = () => {
    setOnboardingHelpDismissed(true);
  };
  const [quickAddSelected, setQuickAddSelected] = useState<Set<string>>(new Set());
  const [quickAddBusy, setQuickAddBusy] = useState(false);

  const openVendorSettingsFromIncompleteCard = (vendorId: string) => {
    setSearchQuery("");
    setSidebarOpenForVendorId(vendorId);
    setSettingsOpenForVendorId(vendorId);
  };

  const clearSettingsOpenForVendorId = useCallback(() => {
    setSettingsOpenForVendorId(null);
  }, []);

  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(org?.id, vendorId, vendorSettings);
    },
    onSuccess: refreshVendors,
    messages: { defaultError: "Couldn't save your changes. Please try again." },
  });

  const updateVendor = async (updateVendorArgs: UpdateVendorArgs) => {
    const previous = vendorsState?.find((v) => v.vendorId === updateVendorArgs.vendorId);
    setVendorsState((prev) =>
      prev.map((vendor) => {
        if (vendor.vendorId === updateVendorArgs.vendorId) {
          let updatedVendor = { ...vendor, ...updateVendorArgs.vendorSettings };
          // Re-create the classification DTO object for the local state (normally this is done by the API, so to be consistent we apply the same mutation to our local state)
          let newClassification = updatedVendor.classificationOptions.find(
            (s) => s.id === updatedVendor.classificationId,
          );
          if (newClassification) {
            updatedVendor.classification = newClassification;
          }
          return updatedVendor;
        }
        return vendor;
      }),
    );

    // Ensure we convert the classification DTO object to the classification ID (if a new classification ID wasn't provided)
    if (
      !updateVendorArgs?.vendorSettings?.classificationId &&
      "classification" in updateVendorArgs.vendorSettings
    ) {
      updateVendorArgs.vendorSettings.classificationId =
        // @ts-ignore - we know that if the classification property exists, it's a DTO object and must have an ID
        updateVendorArgs.vendorSettings.classification?.id;
    }
    try {
      await updateDataRecipient(updateVendorArgs);
    } catch (e) {
      // The save failed (and already showed an error): undo the optimistic update.
      if (previous) {
        setVendorsState((prev) =>
          prev.map((v) => (v.vendorId === previous.vendorId ? previous : v)),
        );
      }
      throw e;
    }
  };

  const [dataSources, dataSourcesRequest, refreshDataSources] = useOrgDataSources(org?.id);
  const [dataSourcesState, setDataSourcesState] = useState<OrganizationDataSourceDto[] | null>(
    null,
  );
  useEffect(() => {
    if (dataSources && dataSourcesState === null) {
      setDataSourcesState(
        dataSources.filter((source) => {
          if (
            !shouldShowInInventoryContext(
              source.collectionContext,
              config.collectionContext,
              config.includeEmptyContext,
            )
          ) {
            return false;
          }

          // Don't show survey-managed built-in sources, which aren't vendors even though they're represented as vendors in the database.
          if (
            source.vendorKey == SOURCE_KEY_CONSUMER_DIRECTLY ||
            source.vendorKey == SOURCE_KEY_OTHER_INDIVIDUALS
          ) {
            return false;
          }

          return true;
        }),
      );
    }
  }, [config.collectionContext, config.includeEmptyContext, dataSources, dataSourcesState]);

  const currentInventoryVendorIds = useMemo(() => {
    const ids = new Set<string>();
    allOrganizationVendors
      ?.filter((vendor) =>
        shouldShowInInventoryContext(
          vendor.collectionContext,
          config.collectionContext,
          config.includeEmptyContext,
        ),
      )
      .forEach((vendor) => ids.add(vendor.vendorId));
    dataSources
      ?.filter((source) =>
        shouldShowInInventoryContext(
          source.collectionContext,
          config.collectionContext,
          config.includeEmptyContext,
        ),
      )
      .forEach((source) => ids.add(source.vendorId));
    // Include optimistic local state so the modal does not re-offer just-added rows.
    vendorsState
      ?.filter((vendor) =>
        shouldShowInInventoryContext(
          vendor.collectionContext,
          config.collectionContext,
          config.includeEmptyContext,
        ),
      )
      .forEach((vendor) => ids.add(vendor.vendorId));
    dataSourcesState
      ?.filter((source) =>
        shouldShowInInventoryContext(
          source.collectionContext,
          config.collectionContext,
          config.includeEmptyContext,
        ),
      )
      .forEach((source) => ids.add(source.vendorId));
    return ids;
  }, [
    allOrganizationVendors,
    config.collectionContext,
    config.includeEmptyContext,
    dataSources,
    dataSourcesState,
    vendorsState,
  ]);

  const existingRecipientVendorIds = useMemo(
    () => new Set(allOrganizationVendors?.map((vendor) => vendor.vendorId) ?? []),
    [allOrganizationVendors],
  );

  const reusableConsumerOnlyVendorIds = useMemo(() => {
    const ids = new Set<string>();
    if (!config.showConsumerOnlyCustomVendorsAndStorageLocations) {
      return ids;
    }

    // HR-only exception: reuse Consumer custom/storage recipients by adding EMPLOYEE context.
    allOrganizationVendors
      ?.filter(
        (vendor) =>
          isConsumerOnlyContext(vendor.collectionContext) &&
          (vendor.isCustom || isStorageLocation(vendor)) &&
          !currentInventoryVendorIds.has(vendor.vendorId),
      )
      .forEach((vendor) => ids.add(vendor.vendorId));

    // Also allow Consumer-only custom data sources that are absent from recipient catalog.
    dataSources
      ?.filter(
        (source) =>
          isConsumerOnlyContext(source.collectionContext) &&
          source.isCustom &&
          !currentInventoryVendorIds.has(source.vendorId),
      )
      .forEach((source) => ids.add(source.vendorId));

    return ids;
  }, [
    allOrganizationVendors,
    config.showConsumerOnlyCustomVendorsAndStorageLocations,
    currentInventoryVendorIds,
    dataSources,
  ]);

  // A vendor is eligible when it is absent from this inventory, even if the organization already uses it in
  // the opposite context. Every catalog entry point uses this same predicate.
  const canAddVendorToCurrentInventory = useCallback(
    (vendorId: string) => !currentInventoryVendorIds.has(vendorId),
    [currentInventoryVendorIds],
  );

  // Allow other pages (e.g. Cookie Scanner) to navigate here with location.state to open Add Vendor modal with pre-selected vendors
  // Latch as a safeguard to prevent modal from opening twice (use useRef instead of useState to avoid extra render)
  const hasConsumedAddVendorNavState = useRef(false);
  useEffect(() => {
    if (!location.state?.openAddVendorModal) {
      return;
    }

    // Safeguard to prevent this from triggering again
    if (hasConsumedAddVendorNavState.current) {
      return;
    }

    // Wait for vendors + data sources so current-inventory eligibility is complete before filtering.
    if (
      !vendorCatalogRequest.success ||
      !allOrganizationVendorsRequest.success ||
      !dataSourcesRequest.success
    ) {
      return;
    }

    const preselectedVendorIds = location.state.preselectedVendorIds ?? [];
    const preselectedVendors: SelectedVendor[] = preselectedVendorIds
      .filter(canAddVendorToCurrentInventory)
      .map((id) => vendorCatalog.find((vendor) => vendor.id === id))
      .filter((vendor): vendor is VendorDto => vendor != null)
      .map((vendor) => ({
        id: vendor.id,
        vendorKey: vendor.vendorKey ?? undefined,
        isNewVendor: false,
        name: vendor.name,
        logoUrl: vendor.logoUrl,
      }));

    hasConsumedAddVendorNavState.current = true;
    setAddVendorCategory(null);
    setInitialSelectedVendors(preselectedVendors);
    setIsAddVendorModalOpen(true);

    // Replace the URL without state so refresh/back doesn't re-open it
    history.replace(location.pathname);
  }, [
    allOrganizationVendorsRequest.success,
    canAddVendorToCurrentInventory,
    dataSourcesRequest.success,
    history,
    location.pathname,
    location.state,
    vendorCatalog,
    vendorCatalogRequest.success,
  ]);

  const removeVendor = async (vendor: VendorInventoryItem) => {
    setVendorsState((prev) => prev.filter((v) => v.vendorId !== vendor.vendorId));
    setDataSourcesState((prev) => prev.filter((v) => v.vendorId !== vendor.vendorId));
    await removeVendorCompletely(org?.id, vendor.vendorId);
    refreshVendors();
    refreshDataSources();
    snackbar(`${vendor.name} was successfully removed.`, { variant: "success" });
  };

  const addDataSource = async (vendorId: string) => {
    const newSource = await Api.orgDataSource.addOrgDataSource(org?.id, {
      vendorId,
      collectionContext,
    });
    setDataSourcesState((prev) => {
      const existingIndex = prev.findIndex((ds) => ds.vendorId === vendorId);
      if (existingIndex === -1) {
        return [...prev, newSource];
      }
      return prev.map((ds) => (ds.vendorId === vendorId ? newSource : ds));
    });
    refreshDataSources();
  };
  const removeDataSource = async (vendorId: string) => {
    // Losing its last context deletes the source and the categories received from it, as the
    // confirmation says. The vendor itself stays: it is still a recipient.
    await Api.orgDataSource.removeOrgDataSource(org?.id, vendorId, collectionContext);
    setDataSourcesState((prev) =>
      prev
        .map((ds) =>
          ds.vendorId === vendorId
            ? {
                ...ds,
                collectionContext: ds.collectionContext.filter(
                  (c) => c !== config.collectionContext,
                ),
              }
            : ds,
        )
        .filter((ds) =>
          shouldShowInInventoryContext(
            ds.collectionContext,
            config.collectionContext,
            config.includeEmptyContext,
          ),
        ),
    );
    refreshDataSources();
  };

  const addRecipient = async (vendorId: string) => {
    const existingRecipient =
      vendorsState?.find((vendor) => vendor.vendorId === vendorId) ??
      allOrganizationVendors?.find((vendor) => vendor.vendorId === vendorId);
    // Empty context implicitly belongs to Consumer; preserve it when adding HR membership.
    const contextToAdd: CollectionContext[] =
      existingRecipient?.collectionContext.length === 0 && config.collectionContext === "EMPLOYEE"
        ? ["CONSUMER", "EMPLOYEE"]
        : collectionContext;
    const newRecipient = await Api.organizationVendor.addOrUpdateVendor(
      org?.id,
      vendorId,
      addRecipientContextSettings(contextToAdd, existingRecipient),
    );
    setVendorsState((prev) => {
      const existingIndex = prev.findIndex((v) => v.vendorId === vendorId);
      if (existingIndex === -1) {
        return [...prev, newRecipient];
      }
      return prev.map((v) => (v.vendorId === vendorId ? newRecipient : v));
    });
    refreshVendors();
    return newRecipient;
  };

  // Recipient role only: the vendor leaves this context's Vendor List but keeps any source role.
  // Losing its last context deletes the recipient row (an empty context would still read as
  // Consumer).
  const removeRecipientOnly = async (vendorId: string) => {
    await Api.organizationVendor.removeVendor(
      org?.id,
      vendorId,
      collectionContext,
      /* deleteOnEmpty = */ true,
      /* removeFromDataMap = */ false,
      /* keepDataSource = */ true,
    );
    setVendorsState((prev) =>
      prev
        .map((v) =>
          v.vendorId === vendorId
            ? {
                ...v,
                collectionContext: v.collectionContext.filter(
                  (c) => c !== config.collectionContext,
                ),
              }
            : v,
        )
        .filter(
          (v) =>
            (v.vendorId !== vendorId || v.collectionContext.length > 0) &&
            shouldShowInInventoryContext(
              v.collectionContext,
              config.collectionContext,
              config.includeEmptyContext,
            ),
        ),
    );
    refreshVendors();
  };

  const addCatalogRecipientToCurrentInventory = async (vendorId: string) => {
    // Recheck at click time so a stale shortcut cannot bypass the same eligibility rule used by the modal.
    if (!canAddVendorToCurrentInventory(vendorId)) {
      return;
    }
    await addRecipient(vendorId);
  };

  const allInventoryItems: VendorInventoryItem[] = useMemo(() => {
    if (!vendorsState || !dataSourcesState) return [];
    return buildVendorInventoryItems({
      recipients: vendorsState,
      dataSources: dataSourcesState,
      collectionContext: config.collectionContext,
      includeEmptyContext: config.includeEmptyContext,
    });
  }, [config.collectionContext, config.includeEmptyContext, vendorsState, dataSourcesState]);

  const nonStorageInventoryItems = useMemo(
    () => allInventoryItems.filter((v) => !v.isInternalDataStorageLocation),
    [allInventoryItems],
  );
  const hasOnlyTruevaultNonStorageVendor =
    nonStorageInventoryItems.length === 1 &&
    nonStorageInventoryItems[0].vendorKey === VENDOR_KEY_TRUEVAULT;

  useEffect(() => {
    let cancelled = false;
    setTrueScanDetailsLoaded(false);

    if (
      !config.showConsumerOnboarding ||
      showOnboardingView ||
      scanInProgress ||
      !hasOnlyTruevaultNonStorageVendor
    ) {
      return;
    }

    void getTrueScanDetails(false)
      .then((details) => {
        if (!cancelled && details) {
          setTrueScanDetailsLoaded(true);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch TrueScan details:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [
    config.showConsumerOnboarding,
    getTrueScanDetails,
    hasOnlyTruevaultNonStorageVendor,
    scanInProgress,
    showOnboardingView,
  ]);

  const [nonEmploymentCollectionGroups] = useNonEmploymentCollectionGroups(
    org?.id,
    /* onlyUseSettled = */ true,
  );
  const [employmentCollectionGroups] = useEmploymentCollectionGroups(
    org?.id,
    /* onlyUseSettled = */ true,
  );
  const collectionGroupsForContext =
    config.collectionContext === "EMPLOYEE"
      ? employmentCollectionGroups
      : nonEmploymentCollectionGroups;
  const hasAnyCollectedPics = collectionGroupsForContext?.some((group) => group.inUse) ?? false;
  const setGoogleAnalyticsDataSharingAnswer = async (newAnswer: boolean) => {
    await setSellingAndSharingAnswer("google-analytics-data-sharing", newAnswer ? "true" : "false");
    await refreshSellingAndSharingAnswers();
    // Since changing the GA Data Sharing answer also changes properties on the GA data recipient, reload only the GA vendor in our local state
    const gaVendorId = findGoogleAnalytics(vendorsState)?.vendorId;
    if (gaVendorId) {
      const updatedGaVendor = await Api.organizationVendor.getVendorDetails(org?.id, gaVendorId);
      setVendorsState((prev) => prev.map((v) => (v.vendorId === gaVendorId ? updatedGaVendor : v)));
    }
  };
  const setShopifySNIAnswer = async (newAnswer: boolean) => {
    await setSellingAndSharingAnswer(SHOPIFY_SNI_SLUG, newAnswer ? "true" : "false");
    // Since changing the SNI answer also adds/removes the SNI vendor, reload the entire page to make sure we get the most recent list of vendors
    window.location.reload();
  };

  const incompleteVendors = useMemo(() => {
    if (!vendorsState || !sellingAndSharingAnswers) {
      return [];
    }

    return vendorsState.filter(
      (v) =>
        !isStorageLocation(v) &&
        !isVendorInventoryComplete(v, sellingAndSharingAnswers, config.collectionContext),
    );
  }, [config.collectionContext, sellingAndSharingAnswers, vendorsState]);

  const incompleteVendorIds = useMemo(
    () =>
      config.showIncompleteVendorWarning
        ? new Set(incompleteVendors.map((v) => v.vendorId))
        : new Set<string>(),
    [config.showIncompleteVendorWarning, incompleteVendors],
  );

  // A vendor can leave the list while its Vendor Settings are open (e.g. "Remove as recipient"
  // unmounts the row and its modal without a close). Drop the stale id so the footer comes back
  // and the row can't reopen its modal if the vendor is added again.
  useEffect(() => {
    if (
      sidebarOpenForVendorId != null &&
      !allInventoryItems.some((v) => v.vendorId === sidebarOpenForVendorId)
    ) {
      setSidebarOpenForVendorId(null);
    }
  }, [allInventoryItems, sidebarOpenForVendorId]);

  const isSidebarOpen =
    sidebarOpenForVendorId != null &&
    allInventoryItems.some((v) => v.vendorId === sidebarOpenForVendorId);

  useEffect(() => {
    onFooterStateChange?.({
      showFooter: showOnboardingFooter && !isSidebarOpen,
      nextDisabled: incompleteVendors.length > 0 || showOnboardingView,
      isSidebarOpen,
      hasOnlyTruevaultVendor:
        allInventoryItems.length === 1 && allInventoryItems[0].vendorKey === VENDOR_KEY_TRUEVAULT,
    });
  }, [
    allInventoryItems,
    incompleteVendors.length,
    isSidebarOpen,
    onFooterStateChange,
    showOnboardingFooter,
    showOnboardingView,
  ]);

  // The answer that finishes the last incomplete vendor is usually given inside its Vendor
  // Settings, a tab or more before Done, so hold the announcement until those settings close.
  const previousIncompleteVendorsRef = useRef<OrganizationDataRecipientDto[] | null>(null);
  const [pendingVendorListCompletion, setPendingVendorListCompletion] =
    useState<VendorListCompletion | null>(null);
  useEffect(() => {
    if (vendorsState === null) {
      return;
    }
    const completion = vendorListJustCompleted({
      previousIncomplete: previousIncompleteVendorsRef.current,
      incomplete: incompleteVendors,
      vendors: vendorsState,
    });
    previousIncompleteVendorsRef.current = incompleteVendors;
    if (completion) {
      setPendingVendorListCompletion(completion);
    } else if (incompleteVendors.length > 0) {
      setPendingVendorListCompletion(null);
    }
  }, [incompleteVendors, vendorsState]);

  useEffect(() => {
    if (!pendingVendorListCompletion || isSidebarOpen) {
      return;
    }
    setPendingVendorListCompletion(null);
    onVendorListComplete?.({ ...pendingVendorListCompletion, showOnboardingFooter });
  }, [isSidebarOpen, onVendorListComplete, pendingVendorListCompletion, showOnboardingFooter]);

  if (
    vendorsState === null ||
    dataSourcesState === null ||
    !sellingAndSharingAnswers ||
    allOrganizationVendors === undefined
  ) {
    return <PageLoading />;
  }

  // Vendors currently shown in the table (after applying the search filter). Drives both the table and the count
  // pill so they stay in sync as the user types.
  const visibleVendors = allInventoryItems.filter((v) =>
    v.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Every vendor category available in the catalog, excluding data storage locations (they get their own table
  // section). Consumer inventory excludes known employment-only categories; HR keeps the full catalog because
  // vendors outside the curated HR category list may still handle employee data.
  const employmentCategories = new Set(KNOWN_EMPLOYMENT_VENDOR_CATEGORIES.map((c) => c.category));
  const shouldIncludeCatalogCategory = (category: string) =>
    config.categoryGroupMode === "hr" || !employmentCategories.has(category);
  const catalogCategories = Array.from(
    new Set(
      (vendorCatalog || [])
        .map((v) => v.category)
        .filter((category): category is string => Boolean(category))
        .filter((category) => category !== DATA_STORAGE_LOCATION_CATEGORY)
        .filter(shouldIncludeCatalogCategory)
        .map((category) => (isLegacyContractorCategory(category) ? "Contractor" : category)),
    ),
  );

  const nonStorageVendorCount = nonStorageInventoryItems.length;

  // First-run help shows on the table view for sparse inventories (right after onboarding / a scan). Established
  // orgs (many vendors) never see it, and once dismissed (✕ or a quick-add) it stays gone for this page load.
  const showOnboardingHelp =
    config.showConsumerOnboarding &&
    !showOnboardingView &&
    !onboardingHelpDismissed &&
    nonStorageVendorCount <= 10 &&
    (hasEverScanned === true || showOnboardingFooter);
  const scanFoundNothing = nonStorageVendorCount <= 1;
  const showScanAgain =
    scanFoundNothing &&
    !scanInProgress &&
    hasOnlyTruevaultNonStorageVendor &&
    trueScanDetailsLoaded;

  // Curated common vendors that exist in this org's catalog and aren't already added.
  const quickAddVendors = QUICK_ADD_VENDOR_NAMES.map((name) =>
    (vendorCatalog || []).find((v) => v.name === name),
  ).filter((v): v is VendorDto => !!v && canAddVendorToCurrentInventory(v.id));

  // Catalog vendors matching the search that aren't added yet — lets the search bar add, not only filter.
  // Explicit name searches span all categories because an employment-category vendor may also handle Consumer
  // data (and vice versa); the page-specific category filter only controls the passive category suggestions.
  const catalogSearchMatches =
    searchQuery.trim().length >= 1
      ? (vendorCatalog || [])
          .filter(
            (v) =>
              v.category !== DATA_STORAGE_LOCATION_CATEGORY &&
              canAddVendorToCurrentInventory(v.id) &&
              v.name.toLowerCase().includes(searchQuery.toLowerCase()),
          )
          .slice(0, 6)
      : [];

  const toggleQuickAdd = (vendorId: string) =>
    setQuickAddSelected((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) {
        next.delete(vendorId);
      } else {
        next.add(vendorId);
      }
      return next;
    });
  const addQuickAddSelected = async () => {
    const ids = Array.from(quickAddSelected).filter(canAddVendorToCurrentInventory);
    if (ids.length === 0) return;
    setQuickAddBusy(true);
    try {
      await Promise.all(ids.map((id) => addCatalogRecipientToCurrentInventory(id)));
      setQuickAddSelected(new Set());
      snackbar(
        `Added ${ids.length} vendor${
          ids.length === 1 ? "" : "s"
        }. Go to their settings to complete setup.`,
        { variant: "success" },
      );
    } finally {
      setQuickAddBusy(false);
    }
  };

  return (
    <div className="tw-boundary">
      <div>
        {/* The first-run onboarding card explains the page itself, so the recipients note only
            appears once that card is gone. */}
        {!showOnboardingView && !showOnboardingHelp && (
          <p className="mb-[16px] max-w-[900px] text-[14px] leading-[20px] text-neutral-600">
            These are your data recipients: vendors your business gives personal data to, such as{" "}
            {config.recipientsHelpExamples}. Vendors you receive data from are managed as sources in{" "}
            <button
              type="button"
              className="bg-transparent border-0 p-0 cursor-pointer text-[14px] font-medium text-primary-600 underline"
              onClick={() => history.push(config.sourcesRoute)}
            >
              {config.sourcesLinkLabel}
            </button>
            .
          </p>
        )}
        {/* First-run onboarding card: one white card explaining what the page is for, with a quick-add of
              common tools. When the scan found nothing, a blue inner card reports it and offers a re-scan. */}
        {!showOnboardingView && showOnboardingHelp && searchQuery.trim() === "" && (
          <div
            style={{
              maxWidth: 1143,
              marginBottom: 48,
              borderRadius: 12,
              border: "1px solid #E8E5EE",
              boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
              backgroundColor: "#fff",
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#1A1726" }}>
                  Let's capture every vendor your business uses
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 14,
                    lineHeight: "21px",
                    color: "#726D83",
                  }}
                >
                  Your data map and privacy policy are built from your vendor list.
                </div>
              </div>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={dismissOnboardingHelp}
                className="cursor-pointer"
                style={{ color: "#726D83", alignSelf: "flex-start" }}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {showScanAgain && (
              <div
                style={{
                  marginTop: 12,
                  borderRadius: 8,
                  padding: "12px 14px",
                  backgroundColor: "#E6F1FB",
                }}
              >
                <div style={{ fontSize: 14, lineHeight: "21px", color: "#0C447C" }}>
                  We couldn't detect your vendors automatically from scanning your website. You can
                  try to rescan, quick select the tools your business uses below, or search our
                  catalog.
                </div>
                <div style={{ marginTop: 12 }}>
                  <Button color="tertiary" size="xs" onClick={() => setShowOnboardingView(true)}>
                    <div className="flex items-center gap-2">
                      <ArrowPathIcon className="h-4 w-4" />
                      <span>Scan again</span>
                    </div>
                  </Button>
                </div>
              </div>
            )}

            {quickAddVendors.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#332E42",
                    marginTop: 20,
                  }}
                >
                  Start with the tools most businesses use
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                  {quickAddVendors.map((v) => {
                    const selected = quickAddSelected.has(v.id);
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => toggleQuickAdd(v.id)}
                        className="cursor-pointer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 7,
                          padding: "5px 11px 5px 7px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: 500,
                          border: `1px solid ${selected ? "#7F77DD" : "#E8E5EE"}`,
                          backgroundColor: selected ? "#EEEDFE" : "#fff",
                          color: selected ? "#3C3489" : "#332E42",
                        }}
                      >
                        {v.logoUrl ? (
                          <img
                            src={v.logoUrl}
                            alt=""
                            style={{ width: 18, height: 18, borderRadius: 4 }}
                          />
                        ) : (
                          <span
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 4,
                              backgroundColor: "#F1EFE8",
                            }}
                          />
                        )}
                        {VENDOR_DISPLAY_LABELS[v.name] ?? v.name}
                        {selected && <CheckIcon className="h-4 w-4" />}
                      </button>
                    );
                  })}
                </div>
                <div style={{ marginTop: 16 }}>
                  <Button
                    color="primary"
                    size="xs"
                    disabled={quickAddSelected.size === 0 || quickAddBusy}
                    onClick={addQuickAddSelected}
                  >
                    <div className="flex items-center gap-2">
                      <PlusIcon className="h-4 w-4" />
                      <span>
                        Add{quickAddSelected.size > 0 ? ` ${quickAddSelected.size}` : ""} selected
                      </span>
                    </div>
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Toolbar row — the only part of the header that pins while the tables scroll. */}
      {!showOnboardingView && (
        <div
          style={{
            position: "sticky",
            top: 0,
            // Stay below the Slideout's z-index (10) so an open vendor panel renders on top of this bar,
            // while still sitting above the tables (rows/category headers are auto).
            zIndex: 9,
            paddingTop: 16,
            paddingBottom: 24,
            // Extend the white background across the page's 64px left padding gutter so the table's drop shadow
            // doesn't bleed out to the left as rows scroll underneath the bar.
            width: "calc(100% + 64px)",
            marginLeft: -64,
            paddingLeft: 64,
            backgroundColor: "white",
          }}
        >
          <div className="flex justify-between items-center max-w-[1143px]">
            <div className="flex items-center gap-[12px]">
              <div className="w-[300px]">
                <div className="relative rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    placeholder="Search Table"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div
                className="rounded-full px-[12px] py-[4px] font-medium text-[14px] whitespace-nowrap"
                style={{ backgroundColor: "#EEEDFE", color: "#3C3489" }}
              >
                {visibleVendors.length} {visibleVendors.length === 1 ? "vendor" : "vendors"}
              </div>
            </div>
            <div className="flex items-center gap-[16px]">
              {config.showConsumerOnboarding && (
                <button
                  type="button"
                  onClick={() => setIsWhoModalOpen(true)}
                  className="font-medium text-[14px] cursor-pointer hover:underline whitespace-nowrap"
                  style={{ color: "#5331C6" }}
                >
                  What goes in your Vendor Inventory?
                </button>
              )}
              <Button color="primary" onClick={() => openAddVendorModal()}>
                <div className="flex items-center gap-2">
                  <PlusIcon className="h-5 w-5" />
                  <span>Add New Vendor</span>
                </div>
              </Button>
            </div>
          </div>
        </div>
      )}

      {showOnboardingView && (
        <div>
          <WebsiteScanCard
            // Skipping the scan goes straight to the table — its onboarding card's quick-add has replaced the
            // old common-vendors modal.
            onSkipScan={() => setShowOnboardingView(false)}
            scanInProgress={scanInProgress}
            onStartScan={async (url: string) => {
              const response = await Api.trueScan.runOnboardingVendorScan(org.id, url);
              if (response.started) {
                setScanInProgress(true);
              }
              return response.started;
            }}
          />
        </div>
      )}

      {!showOnboardingView && (
        <>
          {config.showIncompleteVendorWarning &&
            org.finishedOnboardingLatch && // always hide incomplete card during onboarding (POL-7750)
            incompleteVendors.length > 0 && (
              <div className="rounded-md bg-amber-50 p-4 mb-8 max-w-[1143px]">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon
                      className="h-5 w-5 text-amber-700"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      You have incomplete vendors, click through each to complete their settings.
                    </h3>
                    <ul className="mt-2 space-y-2">
                      {incompleteVendors.map((v) => (
                        <li key={v.vendorId} className="flex items-center gap-2">
                          <img src={v.logoUrl} alt={v.name} className="h-5 w-5 flex-shrink-0" />
                          <Button
                            color="link"
                            variant="noRound"
                            customSizeClassname="h-auto py-0 text-sm underline underline-offset-2"
                            onClick={() => openVendorSettingsFromIncompleteCard(v.vendorId)}
                          >
                            {v.name}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

          {catalogSearchMatches.length > 0 && (
            <div
              style={{
                maxWidth: 1143,
                marginBottom: 24,
                border: "1px solid #E8E5EE",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#F5F4F9",
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  color: "#726D83",
                }}
              >
                Add from catalog · matching "{searchQuery}"
              </div>
              {catalogSearchMatches.map((v) => (
                <div
                  key={v.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 16px",
                    borderTop: "1px solid #E8E5EE",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    {v.logoUrl ? (
                      <img
                        src={v.logoUrl}
                        alt=""
                        style={{ width: 24, height: 24, borderRadius: 4, flexShrink: 0 }}
                      />
                    ) : (
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 4,
                          backgroundColor: "#F1EFE8",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: "#111827" }}>{v.name}</div>
                      <div style={{ fontSize: 12, color: "#726D83" }}>{v.category}</div>
                    </div>
                  </div>
                  <Button
                    color="tertiary"
                    size="xs"
                    onClick={() => addCatalogRecipientToCurrentInventory(v.id)}
                  >
                    <div className="flex items-center gap-2">
                      <PlusIcon className="h-4 w-4" />
                      <span>Add</span>
                    </div>
                  </Button>
                </div>
              ))}
            </div>
          )}

          <VendorInventoryTable
            org={org}
            vendors={visibleVendors}
            allVendors={allInventoryItems}
            incompleteVendorIds={incompleteVendorIds}
            hasAnyCollectedPics={hasAnyCollectedPics}
            updateVendor={updateVendor}
            removeVendor={removeVendor}
            addDataSource={addDataSource}
            removeDataSource={removeDataSource}
            removeRecipientOnly={removeRecipientOnly}
            sellingAndSharingAnswers={sellingAndSharingAnswers}
            setGoogleAnalyticsDataSharingAnswer={setGoogleAnalyticsDataSharingAnswer}
            setShopifySNIAnswer={setShopifySNIAnswer}
            sidebarOpenForVendorId={sidebarOpenForVendorId}
            setSidebarOpenForVendorId={setSidebarOpenForVendorId}
            categoryGroupMode={config.categoryGroupMode}
            pageContext={config.collectionContext}
            settingsOpenForVendorId={settingsOpenForVendorId}
            onSettingsOpened={clearSettingsOpenForVendorId}
            catalogCategories={catalogCategories}
            showEmptyCategories={searchQuery.trim() === ""}
            onAddVendorForCategory={(category) => openAddVendorModal(category)}
          />
        </>
      )}

      {isWhoModalOpen && <WhoShouldBeInInventoryModal onClose={() => setIsWhoModalOpen(false)} />}

      {isAddVendorModalOpen && (
        <AddVendorModal
          organizationId={org.id}
          canAddVendor={canAddVendorToCurrentInventory}
          reusableDataSourceVendorIds={reusableConsumerOnlyVendorIds}
          includeReusableDataSourceCatalog={config.showConsumerOnlyCustomVendorsAndStorageLocations}
          existingContractorNames={
            allOrganizationVendors
              ?.filter((vendor) => vendor.dataRecipientType === "contractor")
              .map((vendor) => vendor.name) ?? []
          }
          initialCategory={addVendorCategory ?? undefined}
          initialSelectedVendors={initialSelectedVendors}
          infoTitle={config.addVendorInfoTitle}
          infoBody={config.addVendorInfoBody}
          onClose={closeAddVendorModal}
          onDone={async (selectedVendors: SelectedVendor[]) => {
            // If the user clicked Done without selecting anything, just close — no API calls, no success toast.
            if (selectedVendors.length === 0) {
              closeAddVendorModal();
              return;
            }

            // Split selectedVendors into existing vendors that have an `id` field and new vendors that need to be created
            const existingDataSourcesToAdd = selectedVendors.filter(
              (vendor) =>
                "id" in vendor &&
                vendor.isFromDataSourceCatalog &&
                !existingRecipientVendorIds.has(vendor.id),
            );
            const existingVendorsToAdd = selectedVendors.filter(
              (vendor) =>
                "id" in vendor &&
                (!vendor.isFromDataSourceCatalog || existingRecipientVendorIds.has(vendor.id)),
            );
            const newVendorsToAdd = selectedVendors.filter((vendor) => !("id" in vendor));

            // First, save all the existing vendors and capture the results
            const [addedExistingVendors] = await Promise.all([
              Promise.all(existingVendorsToAdd.map((vendor) => addRecipient(vendor.id))),
              Promise.all(existingDataSourcesToAdd.map((vendor) => addDataSource(vendor.id))),
            ]);

            // Then, add each new custom vendor and capture the results
            const addedNewVendors = await Promise.all(
              newVendorsToAdd.map((v) => {
                const createCustomVendorRequest: AddOrUpdateCustomVendorDto = {
                  name: v.name,
                  dataRecipientType: v.newVendorType,
                  category: v.category,
                  collectionContext,
                };

                if (v.newVendorType === "contractor") {
                  createCustomVendorRequest.classificationId =
                    vendorsState[0]?.classificationOptions?.find(
                      (option) => option.slug === "contractor",
                    )?.id;
                }

                return Api.organizationVendor.addCustomVendor(org.id, createCustomVendorRequest);
              }),
            );
            if (addedNewVendors.length > 0) {
              await refreshVendorCatalog();
            }

            // addRecipient already upserts existing catalog vendors. Add custom recipients without duplicating
            // anything returned by a concurrent recipient update.
            setVendorsState((prevState) => {
              const recipientsByVendorId = new Map(
                prevState.map((vendor) => [vendor.vendorId, vendor]),
              );
              [...addedExistingVendors, ...addedNewVendors].forEach((vendor) =>
                recipientsByVendorId.set(vendor.vendorId, vendor),
              );
              return Array.from(recipientsByVendorId.values());
            });

            // Since adding Shopify Audiences / SNI vendor has survey question side-effects, reload the entire page when it's added
            if (
              existingVendorsToAdd.find((v) => v.vendorKey === VENDOR_KEY_SHOPIFY_AUDIENCES) !=
              undefined
            ) {
              window.location.reload();
            }

            closeAddVendorModal();

            // Wrap in setTimeout so the toast shows after the modal has been fully removed (otherwise it gets covered up by the modal's overlay)
            setTimeout(() => {
              snackbar("Vendors successfully added. Go to their settings to complete setup.", {
                variant: "success",
              });
            }, 0);
          }}
        />
      )}
    </div>
  );
}

function VendorInventoryTable({
  org,
  vendors,
  allVendors,
  incompleteVendorIds,
  hasAnyCollectedPics,
  updateVendor,
  removeVendor,
  addDataSource,
  removeDataSource,
  removeRecipientOnly,
  sellingAndSharingAnswers,
  setGoogleAnalyticsDataSharingAnswer,
  setShopifySNIAnswer,
  sidebarOpenForVendorId,
  setSidebarOpenForVendorId,
  categoryGroupMode,
  pageContext,
  catalogCategories,
  showEmptyCategories,
  onAddVendorForCategory,
  settingsOpenForVendorId,
  onSettingsOpened,
}: {
  org: OrganizationDto;
  vendors: VendorInventoryItem[];
  allVendors: VendorInventoryItem[];
  incompleteVendorIds: Set<string>;
  hasAnyCollectedPics: boolean;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  removeVendor: (vendor: VendorInventoryItem) => Promise<void>;
  addDataSource: (vendorId: string) => Promise<void>;
  removeDataSource: (vendorId: string) => Promise<void>;
  removeRecipientOnly: (vendorId: string) => Promise<void>;
  sellingAndSharingAnswers: Record<string, string>;
  setGoogleAnalyticsDataSharingAnswer: (newAnswer: boolean) => Promise<void>;
  setShopifySNIAnswer: (newAnswer: boolean) => Promise<void>;
  sidebarOpenForVendorId: string | null;
  setSidebarOpenForVendorId: (id: string | null) => void;
  categoryGroupMode: VendorCategoryGroupMode;
  pageContext: CollectionContext;
  /** All vendor categories from the catalog; unused ones render as empty "add vendors" tables. */
  catalogCategories: string[];
  /** Hide the empty-category tables while a search is active. */
  showEmptyCategories: boolean;
  onAddVendorForCategory: (category: string) => void;
  /** Vendor whose settings slideout should open on load (deep link from e.g. the incomplete-vendors list). */
  settingsOpenForVendorId: string | null;
  onSettingsOpened: () => void;
}) {
  // Which category mini-tables are collapsed (chevron toggled). Collapsed tables show only their header row.
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const toggleCategory = (categoryName: string) =>
    setCollapsedCategories((prev) => ({ ...prev, [categoryName]: !prev[categoryName] }));

  // Per-category sort direction for the vendor-name column (undefined/false = A→Z, true = Z→A).
  const [categorySortDesc, setCategorySortDesc] = useState<Record<string, boolean>>({});
  const toggleCategorySort = (categoryName: string) =>
    setCategorySortDesc((prev) => ({ ...prev, [categoryName]: !prev[categoryName] }));

  const mainVendors = useMemo(
    () => vendors.filter((v) => !v.isInternalDataStorageLocation),
    [vendors],
  );
  const internalStorageVendors = useMemo(
    () => vendors.filter((v) => v.isInternalDataStorageLocation),
    [vendors],
  );

  const existingCategories = new Set(vendors.map((v) => v.category).filter((x) => x));

  // Group vendors by their specific category (e.g. "Ad Network", "Payment Processor") rather than by the
  // broader category group. The category is shown as the group header, so the per-row Category column is dropped.
  const groupedVendors = useMemo(() => {
    const groups: { [category: string]: VendorInventoryItem[] } = {};
    mainVendors.forEach((vendor) => {
      const category = isLegacyContractorCategory(vendor.category)
        ? "Contractor"
        : vendor.category || "Other";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(vendor);
    });
    Object.keys(groups).forEach((category) => {
      groups[category].sort((a, b) => a.name.localeCompare(b.name));
    });
    return groups;
  }, [mainVendors]);

  const sortedGroupNames = useMemo(() => {
    return Object.keys(groupedVendors).sort((a, b) => a.localeCompare(b));
  }, [groupedVendors]);

  const sortedInternalStorageVendors = useMemo(() => {
    return [...internalStorageVendors].sort((a, b) => a.name.localeCompare(b.name));
  }, [internalStorageVendors]);

  const flatSortedVendors = useMemo(() => {
    return [
      ...sortedGroupNames.flatMap((groupName) => groupedVendors[groupName]),
      ...sortedInternalStorageVendors,
    ];
  }, [sortedGroupNames, groupedVendors, sortedInternalStorageVendors]);

  // Stable, so a row's "sidebar opened" effect doesn't re-run on every table render.
  const closeSidebar = useCallback(
    () => setSidebarOpenForVendorId(null),
    [setSidebarOpenForVendorId],
  );

  const renderRow = (vendor: VendorInventoryItem) => (
    <VendorInventoryTableRow
      key={vendor.vendorId}
      org={org}
      vendor={vendor}
      allVendors={allVendors}
      isIncomplete={incompleteVendorIds.has(vendor.vendorId)}
      hasAnyCollectedPics={hasAnyCollectedPics}
      existingCategories={existingCategories}
      updateVendor={updateVendor}
      removeVendor={removeVendor}
      addDataSource={addDataSource}
      removeDataSource={removeDataSource}
      removeRecipientOnly={removeRecipientOnly}
      sellingAndSharingAnswers={sellingAndSharingAnswers}
      setGoogleAnalyticsDataSharingAnswer={setGoogleAnalyticsDataSharingAnswer}
      setShopifySNIAnswer={setShopifySNIAnswer}
      isSidebarOpen={sidebarOpenForVendorId === vendor.vendorId}
      openSidebar={() => setSidebarOpenForVendorId(vendor.vendorId)}
      closeSidebar={closeSidebar}
      sortedVendors={flatSortedVendors}
      navigateToVendor={(vendorId) => setSidebarOpenForVendorId(vendorId)}
      shouldOpenSettings={settingsOpenForVendorId === vendor.vendorId}
      onSettingsOpened={onSettingsOpened}
      categoryGroupMode={categoryGroupMode}
      pageContext={pageContext}
    />
  );

  // Each category renders as its own self-contained rounded table. The header row (a plain div) carries the
  // category name, a count pill, and a chevron. The vendor rows sit in their own table so they can align with the
  // pinned column header, and are wrapped in a grid-template-rows container that animates the collapse.
  const renderCategoryTable = (categoryName: string, categoryVendors: VendorInventoryItem[]) => {
    const isCollapsed = Boolean(collapsedCategories[categoryName]);
    const isDesc = Boolean(categorySortDesc[categoryName]);
    const count = categoryVendors.length;
    const sortedVendors = [...categoryVendors].sort((a, b) =>
      isDesc ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name),
    );
    const columnLabelStyles = "text-neutral-600 font-medium text-[12px] uppercase tracking-[0.4px]";
    return (
      <div
        key={categoryName}
        className={clsx(
          "border border-solid border-neutral-200 rounded-xl shadow-[0px_1px_2px_0px_#1018280D]",
          "max-w-[1143px]",
        )}
        style={{ overflow: "clip" }}
      >
        {/* Header row — column-aligned with the vendor rows below so the labels sit over their columns. */}
        <table className="w-full" style={{ tableLayout: "fixed" }}>
          <VendorTableColgroup />
          <thead>
            <tr className="bg-neutral-0 border-b border-solid border-neutral-200">
              <th className="text-left pl-6" style={{ paddingTop: 11, paddingBottom: 11 }}>
                <div className="flex items-center gap-[8px]">
                  <span className="font-medium text-[14px] text-neutral-800">{categoryName}</span>
                  <ArrowsUpDownIcon
                    className="w-[16px] h-[16px] text-neutral-600 cursor-pointer"
                    onClick={() => toggleCategorySort(categoryName)}
                  />
                  <span
                    className="bg-neutral-200 text-neutral-700 font-medium text-[12px] rounded-full whitespace-nowrap"
                    style={{ padding: "2px 10px" }}
                  >
                    {count} {count === 1 ? "vendor" : "vendors"}
                  </span>
                </div>
              </th>
              <th style={{ paddingTop: 11, paddingBottom: 11 }}>
                {!isCollapsed && <span className={columnLabelStyles}>Targeted Ads</span>}
              </th>
              <th style={{ paddingTop: 11, paddingBottom: 11 }}>
                {!isCollapsed && <span className={columnLabelStyles}>Data Selling</span>}
              </th>
              <th style={{ paddingTop: 11, paddingBottom: 11 }}>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => toggleCategory(categoryName)}
                    className="flex items-center text-neutral-700 cursor-pointer"
                    aria-label={isCollapsed ? `Expand ${categoryName}` : `Collapse ${categoryName}`}
                    aria-expanded={!isCollapsed}
                  >
                    {isCollapsed ? (
                      <ChevronDownIcon className="w-5 h-5" />
                    ) : (
                      <ChevronUpIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </th>
            </tr>
          </thead>
        </table>
        {/* Collapsible vendor rows — grid-template-rows animates the height. */}
        <div
          style={{
            display: "grid",
            gridTemplateRows: isCollapsed ? "0fr" : "1fr",
            transition: "grid-template-rows 0.3s ease",
          }}
        >
          <div style={{ overflow: "hidden", minHeight: 0 }}>
            <table className="w-full" style={{ tableLayout: "fixed" }}>
              <VendorTableColgroup />
              <tbody>{sortedVendors.map((vendor) => renderRow(vendor))}</tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Categories the org isn't using yet — demoted to a compact chip row at the bottom, so they prompt without
  // dominating the page. Each chip opens the category-scoped Add Vendor dialog. Popular categories (per the
  // adoption-derived ranking) sort first and get a recommended treatment; the rest follow alphabetically.
  const emptyCategories = showEmptyCategories
    ? catalogCategories
        .filter((category) => !groupedVendors[category])
        .sort((a, b) => categoryPopularityRank(a) - categoryPopularityRank(b) || a.localeCompare(b))
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {sortedGroupNames.map((groupName) =>
        renderCategoryTable(groupName, groupedVendors[groupName]),
      )}
      {internalStorageVendors.length > 0 &&
        renderCategoryTable("Internal Data Storage Locations", sortedInternalStorageVendors)}

      {emptyCategories.length > 0 && (
        <div style={{ maxWidth: 1143, marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span className="text-neutral-700 font-medium text-[12px] uppercase tracking-wide">
              Add vendors by category
            </span>
            <div style={{ flex: 1, height: 1, backgroundColor: "#E8E5EE" }} />
          </div>
          <div className="text-neutral-700 text-[13px]" style={{ marginBottom: 12 }}>
            Don't see one of your tools above? Add it under its category.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {emptyCategories.map((categoryName) => {
              // Popular categories read as recommended: brand-purple tint + sparkle instead of the neutral chip.
              // Colors mirror the aquilo secondary button (primary-100/200/700 — tokens aren't in the compiled
              // utility bundle, so inline hex).
              const isPopular = isPopularCategory(categoryName);
              return (
                <button
                  key={categoryName}
                  type="button"
                  onClick={() => onAddVendorForCategory(categoryName)}
                  className={clsx(
                    "cursor-pointer",
                    isPopular ? "hover:opacity-80" : "bg-white hover:bg-neutral-100",
                  )}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: isPopular ? 500 : 400,
                    border: `1px solid ${isPopular ? "#DFD8F8" : "#D1CED9"}`,
                    backgroundColor: isPopular ? "#F3F0FE" : undefined,
                    color: isPopular ? "#3C1DA5" : "#534E65",
                  }}
                >
                  <PlusIcon className="h-4 w-4" />
                  {categoryName}
                  {isPopular && <SparklesIcon className="h-4 w-4" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function VendorInventoryTableRow({
  org,
  vendor,
  allVendors,
  isIncomplete,
  hasAnyCollectedPics,
  existingCategories,
  updateVendor,
  removeVendor,
  addDataSource,
  removeDataSource,
  removeRecipientOnly,
  sellingAndSharingAnswers,
  setGoogleAnalyticsDataSharingAnswer,
  setShopifySNIAnswer,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
  sortedVendors,
  navigateToVendor,
  shouldOpenSettings,
  onSettingsOpened,
  categoryGroupMode,
  pageContext,
}: {
  org: OrganizationDto;
  vendor: VendorInventoryItem;
  allVendors: VendorInventoryItem[];
  isIncomplete: boolean;
  hasAnyCollectedPics: boolean;
  existingCategories: Set<string>;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  removeVendor: (vendor: VendorInventoryItem) => Promise<void>;
  addDataSource: (vendorId: string) => Promise<void>;
  removeDataSource: (vendorId: string) => Promise<void>;
  removeRecipientOnly: (vendorId: string) => Promise<void>;
  sellingAndSharingAnswers: Record<string, string>;
  setGoogleAnalyticsDataSharingAnswer: (newAnswer: boolean) => Promise<void>;
  setShopifySNIAnswer: (newAnswer: boolean) => Promise<void>;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  sortedVendors: VendorInventoryItem[];
  navigateToVendor: (vendorId: string) => void;
  shouldOpenSettings: boolean;
  onSettingsOpened: () => void;
  categoryGroupMode: VendorCategoryGroupMode;
  pageContext: CollectionContext;
}) {
  const tableCellStyles = clsx("border-b border-solid border-gray-200");
  const tableCell = (
    children: React.ReactNode,
    customStyles: { colWidth: string; px?: string; py?: string },
  ) => (
    <td
      className={tableCellStyles}
      style={{
        width: customStyles.colWidth,
        minWidth: customStyles.colWidth,
        maxWidth: customStyles.colWidth,
        paddingLeft: customStyles.px || "24px",
        paddingRight: customStyles.px || "24px",
        paddingTop: customStyles.py || "16px",
        paddingBottom: customStyles.py || "16px",
        lineHeight: "18px",
      }}
    >
      {children}
    </td>
  );

  const [isDisabledWhileLoading, setIsDisabledWhileLoading] = useState(false);
  const [isVendorDetailsModalOpen, setIsVendorDetailsModalOpen] = useState(false);
  // Avoids stacked modals while tracking whether canceling removal should restore Vendor Settings.
  const [restoreVendorDetailsAfterRemoveCancel, setRestoreVendorDetailsAfterRemoveCancel] =
    useState(false);

  const {
    openConfirmation: openRemoveVendorConfirmation,
    confirmationModal: removeVendorConfirmationModal,
    isRemovable: isVendorRemovableFromInventory,
  } = useRemoveVendorConfirmation({
    vendorName: vendor.name,
    vendorKey: vendor.vendorKey,
    // Only when the confirmation replaced Vendor Settings; the (hidden) slideout button has no
    // Vendor Settings to go back to.
    showBackToVendorSettings: restoreVendorDetailsAfterRemoveCancel,
    onRemove: async () => {
      setRestoreVendorDetailsAfterRemoveCancel(false);
      setIsVendorDetailsModalOpen(false);
      if (!SHOW_VENDOR_SLIDEOUT) {
        closeSidebar();
      }
      await removeVendor(vendor);
    },
    onCancel: () => {
      if (restoreVendorDetailsAfterRemoveCancel) {
        setIsVendorDetailsModalOpen(true);
      } else if (!SHOW_VENDOR_SLIDEOUT) {
        closeSidebar();
      }
      setRestoreVendorDetailsAfterRemoveCancel(false);
    },
  });

  useEffect(() => {
    if (!shouldOpenSettings) return;

    if (vendor.recipientData) {
      setIsVendorDetailsModalOpen(true);
    }

    onSettingsOpened();
  }, [onSettingsOpened, shouldOpenSettings, vendor.recipientData]);

  // With the slideout hidden, "open the sidebar" (row click, arrow, deep link) means open Vendor
  // Settings; closing the modal releases the row so it can be clicked again. Only the moment it
  // opens counts: a re-run while it stays open (e.g. with the remove confirmation showing) must not
  // bring Vendor Settings back.
  const wasSidebarOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isSidebarOpen && !wasSidebarOpenRef.current;
    wasSidebarOpenRef.current = isSidebarOpen;
    if (SHOW_VENDOR_SLIDEOUT || !justOpened) return;
    if (vendor.recipientData) {
      setIsVendorDetailsModalOpen(true);
    } else {
      closeSidebar();
    }
  }, [isSidebarOpen, vendor.recipientData, closeSidebar]);
  const closeVendorDetailsModal = () => {
    setIsVendorDetailsModalOpen(false);
    if (!SHOW_VENDOR_SLIDEOUT) {
      closeSidebar();
    }
  };

  /** Unique HTML `name` prefix for RadioGroups so fields do not collide with the vendor details modal. */
  const slideoutRadioNamePrefix = `vendor-inventory-slideout-${vendor.vendorId}`;

  const currentVendorIndex = sortedVendors.findIndex((v) => v.vendorId === vendor.vendorId);
  const prevVendor = currentVendorIndex > 0 ? sortedVendors[currentVendorIndex - 1] : null;
  const nextVendor =
    currentVendorIndex < sortedVendors.length - 1 ? sortedVendors[currentVendorIndex + 1] : null;

  return (
    <>
      <tr
        key={vendor.vendorId}
        className={clsx(
          "border-t border-solid border-gray-200",
          "cursor-pointer hover:bg-neutral-0",
          {
            "bg-neutral-0": isSidebarOpen,
          },
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        {/* Vendor Name */}
        {tableCell(
          <div className="flex items-center gap-[12px]">
            <img src={vendor.logoUrl} alt={vendor.name} className="h-6 w-6" />
            <span className="text-[#111827] font-medium text-[14px]">{vendor.name}</span>
            {isIncomplete && (
              <BadgedTextPill cellPillDisplay={{ color: "yellow", text: "Incomplete" }} />
            )}
          </div>,
          { colWidth: "473px" },
        )}

        {/* Targeted Advertising */}
        {tableCell(
          <div className="grid place-items-center">
            <BadgedTextPill cellPillDisplay={getTargetedAdsCell(vendor, pageContext)} />
          </div>,
          { colWidth: "310px" },
        )}

        {/* Data Selling */}
        {tableCell(
          <div className="grid place-items-center">
            <BadgedTextPill
              cellPillDisplay={getDataSellingCell(vendor, sellingAndSharingAnswers, pageContext)}
            />
          </div>,
          { colWidth: "310px" },
        )}

        <td className={clsx(tableCellStyles, "w-[50px] max-w-[50px] min-w-[50px]")}>
          <ArrowRightCircleIcon
            className="w-[20px] h-[20px] text-primary-600 cursor-pointer"
            onClick={openSidebar}
          />
        </td>
      </tr>

      {/* Vendor Details Modal */}
      {isVendorDetailsModalOpen && vendor.recipientData && (
        <VendorDetailsModal
          inventoryVendor={vendor}
          org={org}
          allVendors={allVendors}
          hasAnyCollectedPics={hasAnyCollectedPics}
          existingCategories={existingCategories}
          updateVendor={updateVendor}
          removeDataSource={removeDataSource}
          removeRecipientOnly={removeRecipientOnly}
          isDisabledWhileLoading={isDisabledWhileLoading}
          setIsDisabledWhileLoading={setIsDisabledWhileLoading}
          setGoogleAnalyticsDataSharingAnswer={setGoogleAnalyticsDataSharingAnswer}
          setShopifySNIAnswer={setShopifySNIAnswer}
          onClose={closeVendorDetailsModal}
          onNext={closeVendorDetailsModal}
          onDelete={() => {
            // Hide Vendor Settings while removal confirmation is open.
            setIsVendorDetailsModalOpen(false);
            setRestoreVendorDetailsAfterRemoveCancel(true);
            openRemoveVendorConfirmation();
          }}
          deleteDisabled={!isVendorRemovableFromInventory}
          categoryGroupMode={categoryGroupMode}
          pageContext={pageContext}
        />
      )}

      <Slideout
        open={SHOW_VENDOR_SLIDEOUT && isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
      >
        <>
          {isDisabledWhileLoading && (
            <div className="absolute top-0 left-0 w-full h-full bg-white bg-opacity-50 z-10">
              <div className="flex items-center justify-center w-full h-full">
                <CircularProgress size="6em" />
              </div>
            </div>
          )}

          <div
            className="h-full flex leading-[20px] text-[14px] font-normal"
            style={{ flexDirection: "column" }}
          >
            {/* Sidebar header (vendor name + logo + URL) */}
            <div
              className={clsx(
                "h-[180px] flex-shrink-0",
                "bg-neutral-0 px-[24px] py-[12px] border border-b-1 border-gray-200",
              )}
            >
              <div className="flex justify-between items-center py-[12px]">
                <a
                  className={clsx(
                    "flex items-center gap-1 text-primary-800 underline text-[13px]",
                    prevVendor ? "cursor-pointer" : "pointer-events-none opacity-40",
                  )}
                  onClick={() => prevVendor && navigateToVendor(prevVendor.vendorId)}
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Previous Vendor
                </a>
                <a
                  className={clsx(
                    "flex items-center gap-1 text-primary-800 underline text-[13px]",
                    nextVendor ? "cursor-pointer" : "pointer-events-none opacity-40",
                  )}
                  onClick={() => nextVendor && navigateToVendor(nextVendor.vendorId)}
                >
                  Next Vendor
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
              </div>

              <div className="flex justify-between items-center">
                <h3 className="text-gray-900 text-[18px] font-bold">Vendor List Settings</h3>
                {vendor.isRecipient && (
                  <Button
                    color="tertiary"
                    size="xs"
                    onClick={() => setIsVendorDetailsModalOpen(true)}
                  >
                    Vendor Settings
                  </Button>
                )}
              </div>

              <div className="mt-[5px] flex justify-between items-start">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center">
                    <img src={vendor.logoUrl} alt={vendor.name} className="h-5 w-5 flex-shrink-0" />
                    <span className="text-gray-900 font-medium text-[18px] ml-[12px] mr-[12px] truncate">
                      {vendor.name}
                    </span>
                  </div>
                  {vendor.url?.length > 0 && (
                    <div className="mt-1 font-normal text-gray-500 truncate" title={vendor.url}>
                      {vendor.url}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar content */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {/* Vendor Category */}
              <div className="p-[24px]">
                <div className="text-neutral-900 font-medium">Category</div>
                <div className="text-neutral-700 font-normal">
                  {isLegacyContractorCategory(vendor.category) ? "Contractor" : vendor.category}
                </div>
              </div>

              <hr className="border-gray-200" />

              {!vendor.isInternalDataStorageLocation && categoryGroupMode !== "hr" && (
                <>
                  <VendorDataSourceRecipientSection
                    orgId={org.id}
                    vendor={vendor}
                    setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                    addDataSource={addDataSource}
                    removeDataSource={removeDataSource}
                    removeRecipientOnly={removeRecipientOnly}
                  />

                  <hr className="border-gray-200" />
                </>
              )}

              {/* Recipient-specific sections: only show when vendor is a data recipient */}
              {vendor.isRecipient && vendor.recipientData && (
                <>
                  {/* Systems handled by TrueVault Connected System (POL-7958) */}
                  {isConnectedSystemVendor(vendor.recipientData) && (
                    <>
                      <div className="p-[24px]">
                        <div className="text-neutral-900 font-medium">Automated Systems</div>
                        <div className="text-neutral-500 mt-[6px] mb-[12px]">
                          This connector submits privacy requests to other systems on your behalf.
                        </div>
                        <ConnectedSystemVendorsSection
                          org={org}
                          vendor={vendor}
                          allVendors={allVendors}
                          portalAutocompleteDropdown
                        />
                      </div>

                      <hr className="border-gray-200" />
                    </>
                  )}

                  {/* Targeted Advertising / Sharing */}
                  {shouldShowSharing(vendor.recipientData, pageContext) && (
                    <>
                      <div className="p-[24px]">
                        <div className="flex justify-between items-center">
                          <div className="text-neutral-900 font-medium">Targeted Advertising</div>
                          {!doesVendorHaveSharingComplete(vendor.recipientData) && (
                            <BadgedTextPill
                              cellPillDisplay={{ color: "yellow", text: "Incomplete" }}
                            />
                          )}
                        </div>
                        <div className="text-neutral-500 mt-[6px] mb-[12px]">
                          Targeted Advertising (also known as “Sharing”) involves using customer
                          personal information for purposes of behavioral, or interest-based
                          advertising across platforms or devices.
                        </div>
                        <VendorTargetedAdvertisingSection
                          radioGroupNamePrefix={slideoutRadioNamePrefix}
                          vendor={vendor}
                          org={org}
                          allVendors={allVendors}
                          portalAutocompleteDropdown
                          updateVendor={updateVendor}
                        />
                      </div>

                      <hr className="border-gray-200" />
                    </>
                  )}

                  {/* Data Selling */}
                  {shouldShowDataSellingSection(vendor.recipientData, pageContext) && (
                    <div className="p-[24px]">
                      <div className="flex justify-between items-center">
                        <div className="text-neutral-900 font-medium">Data Selling</div>
                        {!doesVendorHaveSellingComplete(
                          vendor.recipientData,
                          sellingAndSharingAnswers,
                        ) && (
                          <BadgedTextPill
                            cellPillDisplay={{ color: "yellow", text: "Incomplete" }}
                          />
                        )}
                      </div>
                      <div className="text-neutral-500 mt-[6px] mb-[12px]">
                        “Selling" includes exchanging personal data for money or anything else of
                        value (e.g., disclosing personal information and receiving a free service or
                        access to other data in exchange).
                      </div>
                      <VendorDataSellingSection
                        radioGroupNamePrefix={slideoutRadioNamePrefix}
                        vendor={vendor}
                        pageContext={pageContext}
                        org={org}
                        sellingAndSharingAnswers={sellingAndSharingAnswers}
                        setGoogleAnalyticsDataSharingAnswer={setGoogleAnalyticsDataSharingAnswer}
                        updateVendor={updateVendor}
                        setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                        beforeGoogleAnalyticsQuestion={
                          !hasGoogleAnalyticsDataSharingAnswer(sellingAndSharingAnswers) ? (
                            <div className="text-right mb-[12px]">
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Incomplete" }}
                              />
                            </div>
                          ) : undefined
                        }
                      />
                    </div>
                  )}

                  <hr className="border-gray-200" />

                  {/* Shopify Network Intelligence */}
                  {isShopify(vendor.recipientData) && (
                    <ShopifySNIQuestion
                      radioGroupNamePrefix={slideoutRadioNamePrefix}
                      sellingAndSharingAnswers={sellingAndSharingAnswers}
                      setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                      setShopifySNIAnswer={setShopifySNIAnswer}
                      incompleteBadgePlacement="inline-question"
                      className="p-[24px]"
                    />
                  )}
                </>
              )}
            </div>

            {/* Sidebar footer */}
            <div className="h-[80px] shrink-0 border-t border-gray-200">
              <div className="px-[24px] py-[16px] flex justify-end gap-[12px]">
                <Button
                  color="warning-secondary"
                  onClick={() => {
                    // Open confirmation without restoring Vendor Settings on cancel.
                    setRestoreVendorDetailsAfterRemoveCancel(false);
                    openRemoveVendorConfirmation();
                  }}
                  disabled={!isVendorRemovableFromInventory}
                >
                  Remove Vendor
                </Button>
                <Button color="primary" onClick={closeSidebar}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        </>
      </Slideout>

      {/* Outside the Slideout on purpose: with the slideout hidden its children never mount, which
          left "Remove Vendor" in Vendor Settings opening nothing. */}
      {removeVendorConfirmationModal}
    </>
  );
}

/**
 * Shared confirmation + side-effect flow for completely removing a vendor.
 * Use when multiple UI entry points (e.g. sidebar button + details modal) should open the same modal.
 */
export function useRemoveVendorConfirmation({
  vendorName,
  vendorKey,
  showBackToVendorSettings = false,
  onRemove,
  onCancel,
}: {
  vendorName: string;
  vendorKey?: string;
  /** Adds a "Vendor Settings" back link that does the same as Cancel. */
  showBackToVendorSettings?: boolean;
  onRemove: () => Promise<void>;
  onCancel?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const openConfirmation = useCallback(() => setIsOpen(true), []);
  const closeConfirmation = useCallback(() => {
    setIsOpen(false);
    onCancel?.();
  }, [onCancel]);

  const handleConfirm = useCallback(async () => {
    setIsOpen(false);
    await onRemove();
    if (vendorRemovalRequiresPageReload(vendorKey)) {
      window.location.reload();
    }
  }, [onRemove, vendorKey]);

  const confirmationModal = isOpen ? (
    <RemoveVendorConfirmationModal
      vendorName={vendorName}
      onConfirm={handleConfirm}
      onCancel={closeConfirmation}
      onBack={showBackToVendorSettings ? closeConfirmation : undefined}
    />
  ) : null;

  return {
    openConfirmation,
    confirmationModal,
    isRemovable: isVendorRemovable(vendorKey),
  };
}

function WebsiteScanCard({
  onSkipScan,
  scanInProgress,
  onStartScan,
}: {
  onSkipScan: () => void;
  scanInProgress: boolean;
  onStartScan: (url: string) => Promise<boolean>;
}) {
  const { getScan } = useTrueScan();
  const [trueScanData, setTrueScanData] = useState<TrueScanResultsDto | null>(null);
  const [scanUrl, setScanUrl] = useState<string>("");
  const [isStarting, setIsStarting] = useState<boolean>(false);

  const fetchScanData = useCallback(
    async (rebake: boolean = false) => {
      try {
        const data = await getScan(rebake);
        setTrueScanData(data);
        if (data?.suggestedWebsiteUrl) {
          setScanUrl((current) => current || data.suggestedWebsiteUrl || "");
        }
      } catch (error) {
        console.error("Error fetching TrueScan data:", error);
      }
    },
    [getScan],
  );

  useEffect(() => {
    void fetchScanData(false);
  }, [fetchScanData]);

  if (!trueScanData) {
    return <Loading />;
  }

  // Shared card shell for both states — matches the surface used by the onboarding help card and the
  // category tables (hairline border + subtle shadow). Sizing lives in an inline style because new Tailwind
  // arbitrary values (w-[560px] etc.) aren't in the compiled utility bundle.
  const cardClasses =
    "bg-white border border-solid border-neutral-200 rounded-xl shadow-[0px_1px_2px_0px_#1018280D]";
  const cardStyle: React.CSSProperties = {
    width: 560,
    maxWidth: "100%",
    margin: "120px auto 0",
    padding: 32,
  };

  if (scanInProgress) {
    return (
      <div className={cardClasses} style={cardStyle}>
        <div className="flex items-center gap-[12px]">
          <CircularProgress size={20} />
          <div className="text-[16px] font-semibold text-neutral-900">Scanning your website…</div>
        </div>
        <div className="mt-[8px] text-[14px] leading-[21px] text-neutral-600">
          This usually takes a minute or two. We'll add the vendors we detect to your vendor list
          automatically when the scan finishes.
        </div>
      </div>
    );
  }

  return (
    <div className={cardClasses} style={cardStyle}>
      {/* Soft icon badge, then heading + supporting copy — same hierarchy as the first-run help card.
          Colors match the aquilo secondary button (primary-100 bg / primary-700 text); inline hex because
          those token utility classes aren't in the compiled bundle. */}
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 48, height: 48, backgroundColor: "#F3F0FE" }}
      >
        <MagnifyingGlassIcon className="h-6 w-6" style={{ color: "#3C1DA5" }} />
      </div>
      <div className="mt-[16px] text-[18px] font-semibold text-neutral-900">
        Get a head start with a website scan
      </div>
      <div className="mt-[4px] text-[14px] leading-[21px] text-neutral-600">
        We'll scan your website and detect the vendors running on it — analytics, ad pixels, chat
        widgets, and other embedded tools — and add them to your inventory. Vendors that don't
        appear on your site (like shipping or back-office tools) can be added manually after.
      </div>

      <div className="mt-[20px] flex items-center gap-[12px]">
        <div className="flex-1">
          <TextInputBase
            field="truescanUrl"
            value={scanUrl}
            placeholder="yourwebsite.com"
            onChange={(e) => setScanUrl(e.target.value)}
            inputWidth="w-full"
          />
        </div>
        <Button
          color="primary"
          disabled={!scanUrl || isStarting}
          onClick={async () => {
            setIsStarting(true);
            try {
              await onStartScan(scanUrl);
            } finally {
              setIsStarting(false);
            }
          }}
        >
          Scan Website
        </Button>
      </div>
      <div className="mt-[8px] text-[13px] text-neutral-500">
        Takes about a minute — you can review and edit everything it finds afterward.
      </div>

      <div className="mt-[24px] pt-[16px] border-t border-solid border-neutral-200 flex items-center justify-between">
        <span className="text-[14px] text-neutral-600">Prefer to do it yourself?</span>
        <Button color="tertiary" onClick={onSkipScan}>
          Add vendors manually
        </Button>
      </div>
    </div>
  );
}
